import React, { useState, useEffect } from 'react'
import Cookies from 'js-cookie'
import api from '../../../config/api'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { getStoredUser, storeUser } from '../../../config/authUtils'
import Topbar from '../../layouts/Topbar'
import Sidebar from '../../layouts/Sidebar'

const COSTwoFactorAuth = () => {
  const [is2FAEnabled, setIs2FAEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [requestingCode, setRequestingCode] = useState(false)
  const [confirmingCode, setConfirmingCode] = useState(false)
  const [disabling, setDisabling] = useState(false)

  const [otpModalOpen, setOtpModalOpen] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [modalError, setModalError] = useState('')
  const [modalMessage, setModalMessage] = useState('')

  const storedUser = getStoredUser()

  const fetch2FAStatus = async () => {
    setLoading(true)
    try {
      const token = Cookies.get('token')
      const response = await api.get('/api/2fa/status/', {
        headers: { Authorization: `Bearer ${token}` }
      })
      const enabled = !!response.data?.is_2fa_enabled
      setIs2FAEnabled(enabled)
      if (storedUser) storeUser({ ...storedUser, is_2fa_enabled: enabled })
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err)
      if (storedUser && typeof storedUser.is_2fa_enabled !== 'undefined') {
        setIs2FAEnabled(!!storedUser.is_2fa_enabled)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetch2FAStatus() }, [])

  const handleRequestEnable = async () => {
    setRequestingCode(true)
    setModalError('')
    setModalMessage('')
    try {
      const token = Cookies.get('token')
      const response = await api.post('/api/2fa/request-enable/', {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setModalMessage(response.data?.detail || 'Verification code sent to your email address.')
      setOtpCode('')
      setOtpModalOpen(true)
      toast.info('Verification code sent to your email address!')
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to request activation code.')
    } finally {
      setRequestingCode(false)
    }
  }

  const handleConfirmEnable = async (e) => {
    e.preventDefault()
    if (!otpCode || otpCode.length < 6) { setModalError('Please enter a valid 6-digit code.'); return }
    setConfirmingCode(true)
    setModalError('')
    try {
      const token = Cookies.get('token')
      const response = await api.post('/api/2fa/confirm-enable/', { otp_code: otpCode }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      })
      toast.success(response.data?.detail || 'Two-factor authentication has been successfully activated!')
      setIs2FAEnabled(true)
      if (storedUser) storeUser({ ...storedUser, is_2fa_enabled: true })
      setOtpModalOpen(false)
      setOtpCode('')
    } catch (err) {
      setModalError(err.response?.data?.detail || err.response?.data?.message || 'Invalid or expired 2FA code.')
    } finally {
      setConfirmingCode(false)
    }
  }

  const handleDisable2FA = async () => {
    setDisabling(true)
    try {
      const token = Cookies.get('token')
      const response = await api.post('/api/2fa/disable/', {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      toast.success(response.data?.detail || 'Two-factor authentication has been successfully deactivated.')
      setIs2FAEnabled(false)
      if (storedUser) storeUser({ ...storedUser, is_2fa_enabled: false })
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to deactivate two-factor authentication.')
    } finally {
      setDisabling(false)
    }
  }

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <ToastContainer position="top-right" autoClose={3500} hideProgressBar={false} />
      <Topbar />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content pb-0">

          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
            <div className="my-auto mb-2 mb-md-0">
              <h4 className="page-title mb-0">Two-Factor Authentication (2FA)</h4>
              <p className="text-muted mb-0 mt-1">Secure your account with an extra layer of email-based security.</p>
            </div>
            <div>
              {loading ? (
                <span className="spinner-border spinner-border-sm text-primary" role="status" />
              ) : is2FAEnabled ? (
                <span className="badge bg-success-subtle text-success border border-success px-3 py-2 fs-13 fw-semibold">
                  <i className="ti ti-shield-check me-1"></i> 2FA Enabled
                </span>
              ) : (
                <span className="badge bg-secondary-subtle text-secondary border border-secondary px-3 py-2 fs-13 fw-semibold">
                  <i className="ti ti-shield-off me-1"></i> 2FA Disabled
                </span>
              )}
            </div>
          </div>

          {/* Main Card */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-transparent border-bottom py-3 d-flex align-items-center justify-content-between">
              <h5 className="card-title mb-0 text-primary fw-bold">
                <i className="ti ti-shield-lock me-2"></i>Security Settings
              </h5>
            </div>
            <div className="card-body p-4">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <h6 className="text-muted fw-normal mb-0">Loading 2FA settings...</h6>
                </div>
              ) : (
                <>
                  <div className="d-flex align-items-start gap-3 mb-4">
                    <div
                      className={`avatar avtar-lg rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${is2FAEnabled ? 'bg-success-subtle text-success' : 'bg-primary-subtle text-primary'}`}
                      style={{ width: '56px', height: '56px' }}
                    >
                      <i className={`ti ${is2FAEnabled ? 'ti-shield-check' : 'ti-shield-lock'} fs-28`} />
                    </div>
                    <div>
                      <h6 className="fw-bold text-dark mb-1">Email-Based 2FA Verification</h6>
                      <p className="text-muted fs-13 mb-0" style={{ lineHeight: 1.6 }}>
                        When Two-Factor Authentication is active, a 6-digit verification code will be sent to your registered email address every time you sign into your account.
                      </p>
                    </div>
                  </div>

                  <div className="border rounded p-3 bg-light mb-4">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                      <div>
                        <span className="fw-semibold text-dark fs-14">Current Account Email: </span>
                        <span className="text-primary fw-medium fs-14">{storedUser?.email || 'Your registered email'}</span>
                      </div>
                      <span className="fs-12 text-muted">Codes valid for 10 minutes</span>
                    </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-end gap-2 border-top pt-3">
                    {is2FAEnabled ? (
                      <button
                        type="button"
                        className="btn btn-outline-danger d-inline-flex align-items-center gap-2"
                        onClick={handleDisable2FA}
                        disabled={disabling || loading}
                      >
                        {disabling ? (
                          <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /><span>Deactivating...</span></>
                        ) : (
                          <><i className="ti ti-shield-off fs-18" /><span>Disable 2FA</span></>
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary d-inline-flex align-items-center gap-2"
                        onClick={handleRequestEnable}
                        disabled={requestingCode || loading}
                      >
                        {requestingCode ? (
                          <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /><span>Sending Code...</span></>
                        ) : (
                          <><i className="ti ti-shield-check fs-18" /><span>Enable 2FA</span></>
                        )}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* OTP Confirmation Modal */}
      {otpModalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', zIndex: 1065 }}
          onClick={(e) => { if (e.target === e.currentTarget && !confirmingCode) setOtpModalOpen(false) }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document" style={{ maxWidth: '440px' }}>
            <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden">
              <div className="modal-header border-bottom bg-primary text-white p-3">
                <div className="d-flex align-items-center gap-2">
                  <i className="ti ti-shield-check fs-22" />
                  <h5 className="modal-title fw-bold mb-0 text-white">Confirm 2FA Activation</h5>
                </div>
                <button type="button" className="btn-close btn-close-white" onClick={() => !confirmingCode && setOtpModalOpen(false)} aria-label="Close" />
              </div>
              <div className="modal-body p-4">
                <p className="fs-14 text-muted mb-3">
                  {modalMessage || 'Verification code sent to your email address to confirm 2FA activation. Valid for 10 minutes.'}
                </p>
                {modalError && (
                  <div className="alert alert-danger border-0 mb-3 fs-13 py-2 px-3 d-flex align-items-center gap-2" role="alert">
                    <i className="ti ti-alert-triangle fs-18 flex-shrink-0" />
                    <span>{modalError}</span>
                  </div>
                )}
                <form onSubmit={handleConfirmEnable}>
                  <div className="mb-4">
                    <label htmlFor="cos_otp_code" className="form-label fw-medium fs-13 text-dark">
                      Enter 6-Digit Verification Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="cos_otp_code"
                      className="form-control form-control-lg text-center fs-18 fw-bold"
                      style={{ letterSpacing: '6px' }}
                      placeholder="123456"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      required
                      autoFocus
                      disabled={confirmingCode}
                    />
                  </div>
                  <div className="d-flex align-items-center justify-content-end gap-2">
                    <button type="button" className="btn btn-light border" onClick={() => setOtpModalOpen(false)} disabled={confirmingCode}>Cancel</button>
                    <button type="submit" className="btn btn-primary d-inline-flex align-items-center gap-2" disabled={confirmingCode || otpCode.length < 6}>
                      {confirmingCode ? (
                        <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /><span>Confirming...</span></>
                      ) : (
                        <><i className="ti ti-check fs-18" /><span>Activate 2FA</span></>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default COSTwoFactorAuth
